// The student's poll interval in a game or a genomgång: shorter only while the student
// is in the middle of something — answers or choices are accepted, or (genomgång) the
// slides follow the teacher on the student's screen — and today's interval otherwise
// (latency plan, fas 1, Dokument/genomgang-spel-latens-plan.md).
//
// Why only then: an open question is when the class is waiting for the teacher's next
// click (facit, the solution) and a stale screen is noticed at once; following the
// slides, each slide change is the same kind of wait (2.0 s, between the two). In the
// lobby, slides the student does not see, or the results, nothing is waited for, and
// the shorter interval would only add load (the read pool, BUG-2026-076).
//
// The server decides how fast "active" may be: every state response carries `pollMs` (lib/poll-hint.ts), passed
// here as `hintMs` and clamped to [active interval, today's interval]. Missing ⇒ today's interval, so an old
// server, a stub or the brake (LATENS_SNABB_POLL=av) all give today's cadence.
//
// Pure and dependency-free apart from the constants (no React, no Prisma, no "@/"):
// useSpelState / useGenomgangState and the load generator (Lasttest/) call the same
// functions, so the generator's traffic follows the site's real cadence. The random
// spread is passed in (`slump` in [0, 1)) so the generator stays deterministic.
//
// Covered by elev-pollintervall.test.ts.

import {
  AKTIV_LEKTION_GENOMGANG_MS,
  AKTIV_LEKTION_I_GENOMGANG_MS,
  AKTIV_LEKTION_IDLE_MS,
  AKTIV_LEKTION_LEKTION_MS,
  AKTIV_LEKTION_SPEL_MS,
  GENOMGANG_ELEV_AKTIV_MIN_MS,
  GENOMGANG_ELEV_AKTIV_SPRIDNING_MS,
  GENOMGANG_ELEV_DOLD_MS,
  GENOMGANG_ELEV_FOLJ_MIN_MS,
  GENOMGANG_ELEV_FOLJ_SPRIDNING_MS,
  GENOMGANG_ELEV_MIN_MS,
  GENOMGANG_ELEV_SPRIDNING_MS,
  SPEL_DOLD_MS,
  SPEL_ELEV_AKTIV_MS,
  SPEL_ELEV_AKTIV_SPRIDNING_MS,
  SPEL_ELEV_MS,
  SPEL_ELEV_SPRIDNING_MS,
} from "./klient-intervall";

/** Game phases in which the server accepts a student's answer or choice:
 *  Opardy's square and bet, the Steg choice (Stegkampen, Summario, PacMathMan), every question. */
export const SPEL_AKTIVA_FASER: ReadonlySet<string> = new Set(["valj", "satsa", "val", "fraga"]);

/** The part of a game view the rule reads (a student's SpelElevVy has it; so does the generator's). */
export interface SpelVyForIntervall {
  status: string;
  state: { fas: string; drag?: unknown };
}

/** Summario's drag with a power-up choice still open (`deadline` set); PacMathMan's drag has none. */
function oppetValIDraget(drag: unknown): boolean {
  return typeof drag === "object" && drag !== null && "deadline" in drag && drag.deadline != null;
}

/** Answers or choices are accepted right now. */
export function spelFasAktiv(vy: SpelVyForIntervall | null): boolean {
  if (!vy || vy.status !== "pagar") return false;
  const { fas, drag } = vy.state;
  return SPEL_AKTIVA_FASER.has(fas) || (fas === "drag" && oppetValIDraget(drag));
}

/** The server's hint clamped to [min, max]; anything that is not a finite number ⇒ null. */
function klamra(hintMs: number | null | undefined, min: number, max: number): number | null {
  return typeof hintMs === "number" && Number.isFinite(hintMs) ? Math.min(max, Math.max(min, hintMs)) : null;
}

/** The wait before a student's next game poll. `hintMs`: the latest response's `pollMs`. */
export function spelElevIntervall(vy: SpelVyForIntervall | null, dold: boolean, slump: number, hintMs: number | null = null): number {
  if (dold) return SPEL_DOLD_MS;
  const aktiv = spelFasAktiv(vy) ? klamra(hintMs, SPEL_ELEV_AKTIV_MS, SPEL_ELEV_MS) : null;
  return aktiv !== null ? aktiv + slump * SPEL_ELEV_AKTIV_SPRIDNING_MS : SPEL_ELEV_MS + slump * SPEL_ELEV_SPRIDNING_MS;
}

/** The part of a genomgång student view the rule reads. */
export interface GenomgangVyForIntervall {
  status: string;
  fas: string;
  /** The slides follow the teacher on the student's screen; absent ⇒ off. */
  elevFoljer?: boolean;
}

/** The question is open for answers (POST /svar accepts only in "ovning"). */
export function genomgangFasAktiv(vy: GenomgangVyForIntervall | null): boolean {
  return vy !== null && vy.status === "pagar" && vy.fas === "ovning";
}

/** The slides follow the teacher (outside "ovning", which is faster still): every slide change reaches the
 *  student's screen, so the student waits for each one (e2e 2026-10-26: p95 3.05 s at today's interval). */
export function genomgangElevFoljer(vy: GenomgangVyForIntervall | null): boolean {
  return vy !== null && vy.status === "pagar" && vy.elevFoljer === true && vy.fas !== "ovning";
}

/** The wait before a student's next genomgång poll. `hintMs`: the latest response's `pollMs`.
 *  Open question 1.4–1.6 s, following the slides 1.9–2.1 s, otherwise 2.3–2.5 s. The one hint is the
 *  server's floor for both faster cadences: each clamps it to [its own interval, today's], so the brake
 *  (hint = today's interval) or a missing hint gives today's cadence in both. */
export function genomgangElevIntervall(
  vy: GenomgangVyForIntervall | null,
  dold: boolean,
  slump: number,
  hintMs: number | null = null,
): number {
  if (dold) return GENOMGANG_ELEV_DOLD_MS;
  if (genomgangFasAktiv(vy)) {
    const aktiv = klamra(hintMs, GENOMGANG_ELEV_AKTIV_MIN_MS, GENOMGANG_ELEV_MIN_MS);
    if (aktiv !== null) return aktiv + slump * GENOMGANG_ELEV_AKTIV_SPRIDNING_MS;
  } else if (genomgangElevFoljer(vy)) {
    const foljer = klamra(hintMs, GENOMGANG_ELEV_FOLJ_MIN_MS, GENOMGANG_ELEV_MIN_MS);
    if (foljer !== null) return foljer + slump * GENOMGANG_ELEV_FOLJ_SPRIDNING_MS;
  }
  return GENOMGANG_ELEV_MIN_MS + slump * GENOMGANG_ELEV_SPRIDNING_MS;
}

/** The `pollMs` hint out of a state response body (full view or { oforandrad }); null when absent. */
export function pollHintFran(data: unknown): number | null {
  const v = typeof data === "object" && data !== null && "pollMs" in data ? data.pollMs : null;
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

/** What the student's lesson bar (/api/student/aktiv-lektion) knows when it picks its next interval. */
export interface AktivLektionLage {
  /** The class has a lesson running (the last answer's `aktiv`). */
  lektion: boolean;
  /** A game is running that the student has not joined. */
  spelVantar: boolean;
  /** The class's running genomgång, or null. */
  genomgangId: string | null;
  /** The page the student is on. */
  sokvag: string;
  /** The pages of the running game, Begreppsjakt, Matte-detektiven and Problemlösning the student has joined ("/spel/<id>" …). */
  egnaAktivitetssidor?: readonly string[];
}

/**
 * LektionsBarForElev's wait before its next poll: 60 s without a lesson, 15 s in a lesson, 5 s while a game or a
 * genomgång waits for the student elsewhere (the popup) — and 30 s on the running genomgång's own page, which
 * polls the genomgång itself and kicks the bar the moment it ends (genomgång performance pass 2026-10-26). The same
 * 30 s on the page of a game, Begreppsjakt or Matte-detektiven the student has joined (2026-10-31, polling-analysen
 * A10): that page's own poll follows the activity and kicks the bar when it ends; a genomgång waiting elsewhere
 * still gets its 5 s.
 */
export function aktivLektionIntervall(l: AktivLektionLage): number {
  if (!l.lektion) return AKTIV_LEKTION_IDLE_MS;
  if (l.spelVantar) return AKTIV_LEKTION_SPEL_MS;
  if (l.genomgangId === null) {
    const paEgenSida = (l.egnaAktivitetssidor ?? []).some((p) => l.sokvag === p || l.sokvag.startsWith(`${p}/`));
    return paEgenSida ? AKTIV_LEKTION_I_GENOMGANG_MS : AKTIV_LEKTION_LEKTION_MS;
  }
  const paSidan = l.sokvag === `/genomgang/${l.genomgangId}` || l.sokvag.startsWith(`/genomgang/${l.genomgangId}/`);
  if (paSidan) return AKTIV_LEKTION_I_GENOMGANG_MS;
  return l.sokvag.startsWith("/genomgang/") ? AKTIV_LEKTION_LEKTION_MS : AKTIV_LEKTION_GENOMGANG_MS;
}
